import React, { useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  useColorScheme,
  TextInput,
  Switch,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const FoodInputConfirmScreen = ({ route, navigation }) => {
  const { foodData } = route.params;
  const isDarkMode = useColorScheme() === 'dark';
  
  const [editedFood, setEditedFood] = useState({
    ...foodData,
    mealTime: '午餐',
    date: new Date(),
    isFavorite: false,
    notes: '',
    quantity: 1, // 份数
  });

  const [activeTab, setActiveTab] = useState('basic'); // 'basic' or 'advanced'

  // 预设份量选项
  const portionOptions = [
    { label: '0.5份', value: 0.5 },
    { label: '1份', value: 1 },
    { label: '1.5份', value: 1.5 },
    { label: '2份', value: 2 },
  ];

  // 预设单位选项
  const unitOptions = [
    { label: '克', value: 'g' },
    { label: '毫升', value: 'ml' },
    { label: '份', value: 'portion' },
    { label: '个', value: 'piece' },
  ];

  // 预设用餐时间选项
  const mealTimeOptions = [
    { label: '早餐', value: '早餐' },
    { label: '午餐', value: '午餐' },
    { label: '晚餐', value: '晚餐' },
    { label: '加餐', value: '加餐' },
  ];

  // 标签选项
  const tagOptions = [
    { label: '家庭自制', value: '家庭自制' },
    { label: '外卖', value: '外卖' },
    { label: '餐厅', value: '餐厅' },
    { label: '加工食品', value: '加工食品' },
  ];

  const [selectedTags, setSelectedTags] = useState([]);

  // 处理份量变化
  const handlePortionChange = (portion) => {
    const newWeight = Math.round(foodData.weight * portion);
    const newCalories = Math.round(foodData.calories * portion);
    const newProtein = Math.round(foodData.protein * portion * 10) / 10;
    const newFat = Math.round(foodData.fat * portion * 10) / 10;
    const newCarbs = Math.round(foodData.carbs * portion * 10) / 10;

    setEditedFood({
      ...editedFood,
      weight: newWeight,
      calories: newCalories,
      protein: newProtein,
      fat: newFat,
      carbs: newCarbs,
      quantity: portion,
    });
  };

  // 处理数值输入
  const handleNumberInput = (field, value) => {
    const numValue = parseFloat(value) || 0;
    setEditedFood({
      ...editedFood,
      [field]: numValue,
    });
  };

  // 处理标签选择
  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  // 保存记录
  const saveRecord = () => {
    // 这里应该有实际的保存逻辑，暂时用Alert模拟
    Alert.alert(
      '保存成功',
      '食物记录已保存',
      [
        {
          text: '继续添加',
          onPress: () => navigation.navigate('FoodInputHome'),
        },
        {
          text: '查看详情',
          onPress: () => navigation.navigate('FoodDetail', { foodData: editedFood }),
        },
        {
          text: '返回主页',
          onPress: () => navigation.navigate('FoodHome'),
          style: 'cancel',
        },
      ],
    );
  };

  return (
    <ScrollView 
      style={{
        flex: 1,
        backgroundColor: isDarkMode ? '#8fa0c1' : '#ffffff',
      }}
      contentContainerStyle={{ padding: 16 }}
    >
      {/* 食物基本信息 */}
      <View style={{ 
        padding: 20, 
        borderRadius: 16, 
        backgroundColor: isDarkMode ? '#a9b6d0' : '#f0f9ff',
        marginBottom: 20,
      }}>
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          marginBottom: 12,
        }}>
          <View style={{
            width: 50,
            height: 50,
            borderRadius: 8,
            backgroundColor: '#f59e0b',
            justifyContent: 'center',
            alignItems: 'center',
            marginRight: 16,
          }}>
            <Ionicons name="restaurant" size={28} color="white" />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{
              fontSize: 20,
              fontWeight: 'bold',
              color: isDarkMode ? 'white' : '#1e3a8a',
            }}>
              {editedFood.name}
            </Text>
            <Text style={{
              fontSize: 15,
              color: isDarkMode ? '#e0e7ff' : '#3b82f6',
            }}>
              {editedFood.weight}克 · {editedFood.calories}千卡
            </Text>
          </View>
          <TouchableOpacity 
            style={{
              padding: 8,
              borderRadius: 20,
              backgroundColor: editedFood.isFavorite 
                ? '#ec4899' 
                : (isDarkMode ? '#6b7280' : '#f3f4f6'),
            }}
            onPress={() => setEditedFood({...editedFood, isFavorite: !editedFood.isFavorite})}
          >
            <Ionicons 
              name={editedFood.isFavorite ? "heart" : "heart-outline"} 
              size={24} 
              color={editedFood.isFavorite ? "white" : (isDarkMode ? 'white' : '#6b7280')} 
            />
          </TouchableOpacity>
        </View>
      </View>

      {/* 切换标签页 */}
      <View style={{
        flexDirection: 'row',
        backgroundColor: isDarkMode ? '#7e8db0' : '#f1f5f9',
        borderRadius: 12,
        padding: 4,
        marginBottom: 16,
      }}>
        <TouchableOpacity
          style={{
            flex: 1,
            padding: 12,
            borderRadius: 8,
            backgroundColor: activeTab === 'basic' 
              ? (isDarkMode ? '#4c51bf' : '#4f46e5') 
              : 'transparent',
            alignItems: 'center',
          }}
          onPress={() => setActiveTab('basic')}
        >
          <Text style={{
            fontWeight: '600',
            color: activeTab === 'basic' ? 'white' : (isDarkMode ? 'white' : '#4b5563'),
          }}>
            基本信息
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={{
            flex: 1,
            padding: 12,
            borderRadius: 8,
            backgroundColor: activeTab === 'advanced' 
              ? (isDarkMode ? '#4c51bf' : '#4f46e5') 
              : 'transparent',
            alignItems: 'center',
          }}
          onPress={() => setActiveTab('advanced')}
        >
          <Text style={{
            fontWeight: '600',
            color: activeTab === 'advanced' ? 'white' : (isDarkMode ? 'white' : '#4b5563'),
          }}>
            扩展信息
          </Text>
        </TouchableOpacity>
      </View>

      {activeTab === 'basic' ? (
        <View>
          {/* 份量选择 */}
          <View style={{
            marginBottom: 24,
          }}>
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              color: isDarkMode ? 'white' : '#1f2937',
              marginBottom: 12,
            }}>
              份量调整
            </Text>
            <View style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              marginBottom: 16,
            }}>
              {portionOptions.map((option, index) => (
                <TouchableOpacity
                  key={index}
                  style={{
                    paddingVertical: 8,
                    paddingHorizontal: 16,
                    borderRadius: 20,
                    backgroundColor: editedFood.quantity === option.value 
                      ? '#2563eb' 
                      : (isDarkMode ? '#7e8db0' : '#f1f5f9'),
                    marginRight: 8,
                    marginBottom: 8,
                  }}
                  onPress={() => handlePortionChange(option.value)}
                >
                  <Text style={{
                    color: editedFood.quantity === option.value 
                      ? 'white' 
                      : (isDarkMode ? 'white' : '#4b5563'),
                    fontWeight: editedFood.quantity === option.value ? '600' : 'normal',
                  }}>
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}>
              <Text style={{
                fontSize: 15,
                color: isDarkMode ? '#e0e0e0' : '#6b7280',
              }}>
                自定义份量:
              </Text>
              <View style={{
                flexDirection: 'row',
                alignItems: 'center',
              }}>
                <TouchableOpacity
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 18,
                    backgroundColor: isDarkMode ? '#7e8db0' : '#f1f5f9',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  onPress={() => {
                    if (editedFood.quantity > 0.1) {
                      handlePortionChange(Math.round((editedFood.quantity - 0.1) * 10) / 10);
                    }
                  }}
                >
                  <Ionicons name="remove" size={20} color={isDarkMode ? 'white' : '#4b5563'} />
                </TouchableOpacity>
                <TextInput
                  style={{
                    padding: 8,
                    borderRadius: 8,
                    backgroundColor: isDarkMode ? '#a9b6d0' : '#f3f4f6',
                    fontSize: 16,
                    width: 60,
                    marginHorizontal: 8,
                    textAlign: 'center',
                    color: isDarkMode ? 'white' : '#1f2937',
                  }}
                  value={editedFood.quantity.toString()}
                  onChangeText={(text) => {
                    const value = parseFloat(text) || 0;
                    handlePortionChange(value);
                  }}
                  keyboardType="numeric"
                />
                <TouchableOpacity
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 18,
                    backgroundColor: isDarkMode ? '#7e8db0' : '#f1f5f9',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  onPress={() => {
                    handlePortionChange(Math.round((editedFood.quantity + 0.1) * 10) / 10);
                  }}
                >
                  <Ionicons name="add" size={20} color={isDarkMode ? 'white' : '#4b5563'} />
                </TouchableOpacity>
              </View>
            </View>
            
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <Text style={{
                fontSize: 15,
                color: isDarkMode ? '#e0e0e0' : '#6b7280',
              }}>
                单位:
              </Text>
              <View style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
              }}>
                {unitOptions.map((option, index) => (
                  <TouchableOpacity
                    key={index}
                    style={{
                      paddingVertical: 6,
                      paddingHorizontal: 12,
                      borderRadius: 16,
                      backgroundColor: isDarkMode ? '#7e8db0' : '#f1f5f9',
                      marginLeft: 8,
                    }}
                  >
                    <Text style={{
                      color: isDarkMode ? 'white' : '#4b5563',
                    }}>
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>

          {/* 营养数据编辑 */}
          <View style={{
            marginBottom: 24,
          }}>
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              color: isDarkMode ? 'white' : '#1f2937',
              marginBottom: 12,
            }}>
              营养数据调整
            </Text>
            
            {/* 重量输入 */}
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}>
              <Text style={{
                fontSize: 15,
                color: isDarkMode ? '#e0e0e0' : '#6b7280',
                width: 80,
              }}>
                重量 (克):
              </Text>
              <TextInput
                style={{
                  flex: 1,
                  padding: 10,
                  borderRadius: 8,
                  backgroundColor: isDarkMode ? '#a9b6d0' : '#f3f4f6',
                  fontSize: 16,
                  color: isDarkMode ? 'white' : '#1f2937',
                }}
                value={editedFood.weight.toString()}
                onChangeText={(text) => handleNumberInput('weight', text)}
                keyboardType="numeric"
              />
            </View>
            
            {/* 热量输入 */}
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}>
              <Text style={{
                fontSize: 15,
                color: isDarkMode ? '#e0e0e0' : '#6b7280',
                width: 80,
              }}>
                热量 (千卡):
              </Text>
              <TextInput
                style={{
                  flex: 1,
                  padding: 10,
                  borderRadius: 8,
                  backgroundColor: isDarkMode ? '#a9b6d0' : '#f3f4f6',
                  fontSize: 16,
                  color: isDarkMode ? 'white' : '#1f2937',
                }}
                value={editedFood.calories.toString()}
                onChangeText={(text) => handleNumberInput('calories', text)}
                keyboardType="numeric"
              />
            </View>
            
            {/* 蛋白质输入 */}
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}>
              <Text style={{
                fontSize: 15,
                color: isDarkMode ? '#e0e0e0' : '#6b7280',
                width: 80,
              }}>
                蛋白质 (克):
              </Text>
              <TextInput
                style={{
                  flex: 1,
                  padding: 10,
                  borderRadius: 8,
                  backgroundColor: isDarkMode ? '#a9b6d0' : '#f3f4f6',
                  fontSize: 16,
                  color: isDarkMode ? 'white' : '#1f2937',
                }}
                value={editedFood.protein.toString()}
                onChangeText={(text) => handleNumberInput('protein', text)}
                keyboardType="numeric"
              />
            </View>
            
            {/* 脂肪输入 */}
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}>
              <Text style={{
                fontSize: 15,
                color: isDarkMode ? '#e0e0e0' : '#6b7280',
                width: 80,
              }}>
                脂肪 (克):
              </Text>
              <TextInput
                style={{
                  flex: 1,
                  padding: 10,
                  borderRadius: 8,
                  backgroundColor: isDarkMode ? '#a9b6d0' : '#f3f4f6',
                  fontSize: 16,
                  color: isDarkMode ? 'white' : '#1f2937',
                }}
                value={editedFood.fat.toString()}
                onChangeText={(text) => handleNumberInput('fat', text)}
                keyboardType="numeric"
              />
            </View>
            
            {/* 碳水化合物输入 */}
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <Text style={{
                fontSize: 15,
                color: isDarkMode ? '#e0e0e0' : '#6b7280',
                width: 80,
              }}>
                碳水 (克):
              </Text>
              <TextInput
                style={{
                  flex: 1,
                  padding: 10,
                  borderRadius: 8,
                  backgroundColor: isDarkMode ? '#a9b6d0' : '#f3f4f6',
                  fontSize: 16,
                  color: isDarkMode ? 'white' : '#1f2937',
                }}
                value={editedFood.carbs.toString()}
                onChangeText={(text) => handleNumberInput('carbs', text)}
                keyboardType="numeric"
              />
            </View>
          </View>
        </View>
      ) : (
        <View>
          {/* 用餐时间选择 */}
          <View style={{
            marginBottom: 24,
          }}>
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              color: isDarkMode ? 'white' : '#1f2937',
              marginBottom: 12,
            }}>
              用餐时间
            </Text>
            <View style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
            }}>
              {mealTimeOptions.map((option, index) => (
                <TouchableOpacity
                  key={index}
                  style={{
                    paddingVertical: 8,
                    paddingHorizontal: 16,
                    borderRadius: 20,
                    backgroundColor: editedFood.mealTime === option.value 
                      ? '#2563eb' 
                      : (isDarkMode ? '#7e8db0' : '#f1f5f9'),
                    marginRight: 8,
                    marginBottom: 8,
                  }}
                  onPress={() => setEditedFood({...editedFood, mealTime: option.value})}
                >
                  <Text style={{
                    color: editedFood.mealTime === option.value 
                      ? 'white' 
                      : (isDarkMode ? 'white' : '#4b5563'),
                    fontWeight: editedFood.mealTime === option.value ? '600' : 'normal',
                  }}>
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          
          {/* 标签选择 */}
          <View style={{
            marginBottom: 24,
          }}>
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              color: isDarkMode ? 'white' : '#1f2937',
              marginBottom: 12,
            }}>
              添加标签
            </Text>
            <View style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
            }}>
              {tagOptions.map((option, index) => (
                <TouchableOpacity
                  key={index}
                  style={{
                    paddingVertical: 8,
                    paddingHorizontal: 16,
                    borderRadius: 20,
                    backgroundColor: selectedTags.includes(option.value) 
                      ? '#8b5cf6' 
                      : (isDarkMode ? '#7e8db0' : '#f1f5f9'),
                    marginRight: 8,
                    marginBottom: 8,
                  }}
                  onPress={() => toggleTag(option.value)}
                >
                  <Text style={{
                    color: selectedTags.includes(option.value) 
                      ? 'white' 
                      : (isDarkMode ? 'white' : '#4b5563'),
                    fontWeight: selectedTags.includes(option.value) ? '600' : 'normal',
                  }}>
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          
          {/* 备注 */}
          <View style={{
            marginBottom: 24,
          }}>
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              color: isDarkMode ? 'white' : '#1f2937',
              marginBottom: 12,
            }}>
              备注
            </Text>
            <TextInput
              style={{
                padding: 12,
                borderRadius: 8,
                backgroundColor: isDarkMode ? '#a9b6d0' : '#f3f4f6',
                fontSize: 16,
                color: isDarkMode ? 'white' : '#1f2937',
                minHeight: 80,
                textAlignVertical: 'top',
              }}
              value={editedFood.notes}
              onChangeText={(text) => setEditedFood({...editedFood, notes: text})}
              placeholder="添加备注信息..."
              placeholderTextColor={isDarkMode ? '#e0e0e0' : '#9ca3af'}
              multiline={true}
            />
          </View>
          
          {/* 收藏选项 */}
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 24,
            padding: 12,
            borderRadius: 8,
            backgroundColor: isDarkMode ? '#7e8db0' : '#f1f5f9',
          }}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Ionicons 
                name="heart" 
                size={22} 
                color="#ec4899" 
                style={{ marginRight: 8 }} 
              />
              <Text style={{
                fontSize: 16,
                color: isDarkMode ? 'white' : '#4b5563',
              }}>
                添加到收藏
              </Text>
            </View>
            <Switch
              value={editedFood.isFavorite}
              onValueChange={(value) => setEditedFood({...editedFood, isFavorite: value})}
              trackColor={{ false: '#d1d5db', true: '#ec4899' }}
              thumbColor={'white'}
            />
          </View>
        </View>
      )}

      {/* 操作按钮 */}
      <View style={{
        flexDirection: 'row',
        marginTop: 8,
      }}>
        <TouchableOpacity
          style={{
            flex: 1,
            padding: 16,
            borderRadius: 12,
            backgroundColor: isDarkMode ? '#7e8db0' : '#f1f5f9',
            alignItems: 'center',
            marginRight: 8,
          }}
          onPress={() => navigation.goBack()}
        >
          <Text style={{
            fontSize: 16,
            fontWeight: 'bold',
            color: isDarkMode ? 'white' : '#4b5563',
          }}>
            返回
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={{
            flex: 2,
            padding: 16,
            borderRadius: 12,
            backgroundColor: '#2563eb',
            alignItems: 'center',
          }}
          onPress={saveRecord}
        >
          <Text style={{
            fontSize: 16,
            fontWeight: 'bold',
            color: 'white',
          }}>
            保存记录
          </Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
};

export default FoodInputConfirmScreen; 